// [S-261 / S-072 / S-073a] Review queue — Legal Ops view of documents in
// `stage3_human` state. Taxonomy (deal type / guild) is auto-detected during
// ingestion by the Stage-2 LLM; this screen surfaces those auto-tags per file
// and lets the reviewer CORRECT them before approving. Approve embeds the
// StructuredContract into the organization's L2 vector collection (→ searchable
// precedent) using the reviewer's tags (which win over the upload batch tags
// and the LLM). Reject moves the doc to `failed`. All calls hit the live
// /review-queue admin endpoints.
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  PageHead,
  Pagination,

  useToast,
  usePagination,
} from "../components";
import { ApiError, getApiErrorMessage, useApiBlob } from "../data/api";
import { SourceViewer } from "../components/SourceViewer";
import {
  useApproveReviewEntry,
  useRejectReviewEntry,
  useReviewQueue,
  type ApproveDimensions,
  type ApproveTags,
  type ReviewQueueEntryRow,
} from "../data/ingestionQuery";
import {
  useStudioVocabulary,
  type PrecedentFacet,
} from "../data/precedentsQuery";
import { useTenantsList } from "../data/tenantsQuery";
import { useState } from "react";

export function ReviewQueue(): JSX.Element {
  const query = useReviewQueue();
  const entries = (query.data ?? []).filter((e) => e.status === "pending");
  const pager = usePagination(entries, 25);
  // Only meaningful for platform_admin (a tenant_admin's queue is already
  // scoped to one tenant server-side, so every row shares the same id) —
  // 403s harmlessly for tenant_admin and the badge is omitted below.
  const studioNameByTenantId = new Map(
    (useTenantsList().data?.tenants ?? []).map((t) => [t.id, t.studioId])
  );

  // [Phase 4] Bulk/onboarding review mode. When a studio ingests its backlog,
  // dozens of high-confidence files land here at once; triaging them one-by-one
  // doesn't scale. Selection + bulk-approve lets the reviewer auto-accept the
  // high-confidence files (LLM-extracted, not flagged for manual extraction) in
  // one action, and focus attention on the genuine novelties (see ReviewRow's
  // novelty badge). Hybrid docs (needsFullHumanExtraction) require per-file
  // clause entry, so they are NOT bulk-selectable.
  const approvableHashes = entries
    .filter((e) => !e.needsFullHumanExtraction)
    .map((e) => e.documentHash);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const bulkApprove = useApproveReviewEntry();
  const toast = useToast();
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(
    null
  );

  const toggle = (hash: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(hash)) next.delete(hash);
      else next.add(hash);
      return next;
    });
  };

  const onBulkApprove = async (): Promise<void> => {
    // Snapshot the selection — the queue refetches (and rows drop) as each
    // approve succeeds, but we iterate the captured hashes.
    const hashes = [...selected].filter((h) => approvableHashes.includes(h));
    if (hashes.length === 0) return;
    setProgress({ done: 0, total: hashes.length });
    let failures = 0;
    for (let i = 0; i < hashes.length; i += 1) {
      try {
        // No tags/dimensions → accept the auto-detected classification as-is.
        await bulkApprove.mutateAsync({ documentHash: hashes[i]! });
      } catch {
        failures += 1;
      }
      setProgress({ done: i + 1, total: hashes.length });
    }
    setSelected(new Set());
    setProgress(null);
    // [S-634] Affirmative confirmation — the approved rows silently drop out of
    // the queue on refetch, so tell the reviewer what actually happened and where
    // to find it. Failures also surface as row-level error pills on re-appearance.
    const approved = hashes.length - failures;
    if (approved > 0) {
      toast.push({
        variant: failures > 0 ? "info" : "success",
        title: `${approved} document${approved === 1 ? "" : "s"} approved${
          failures > 0 ? ` · ${failures} failed` : ""
        }`,
        description: (
          <>
            {/* Plain anchor: a toast renders in a portal OUTSIDE the Router,
                so <Link> has no router context (main.tsx nests ToastProvider
                above BrowserRouter). A full nav to the library is fine here. */}
            Added to your <a href="/precedent">precedent library →</a>
          </>
        ),
      });
    } else if (failures > 0) {
      toast.push({
        variant: "error",
        title: `Couldn't approve ${failures} document${failures === 1 ? "" : "s"}`,
        description: "See the error on each row that reappeared.",
      });
    }
  };

  const busy = progress !== null;

  return (
    <>
      <PageHead
        eyebrow="Legal Ops · review queue"
        title={
          <>
            Stage 3 <em>review backlog.</em>
          </>
        }
        sub={
          <>
            Classification and comparability dimensions are auto-detected on
            ingest — bulk-approve the high-confidence files, or correct and
            approve individually.{" "}
            {query.isLoading
              ? "Loading…"
              : `${entries.length} item${entries.length === 1 ? "" : "s"}.`}
          </>
        }
        cta={
          <Link to="/ingestion" className="btn btn--g btn--s">
            Full ingestion view →
          </Link>
        }
      />

      {(query.isLoading || (query.isFetching && entries.length === 0)) && (
        <div className="ingest__state" role="status">
          Loading queue…
        </div>
      )}
      {query.isError && (
        <Alert label="Couldn't load" tone="error">
          {query.error?.message ?? "unknown error"}
        </Alert>
      )}
      {!query.isLoading && !query.isFetching && !query.isError && entries.length === 0 && (
        <div className="ingest__state" data-testid="queue-empty">
          The review queue is empty. Documents land here when the AI extractor
          flags them for a human pass — typically because confidence is below
          the auto-publish threshold.
        </div>
      )}

      {entries.length > 0 && (
        <>
          <BulkToolbar
            approvableHashes={approvableHashes}
            selected={selected}
            onSelectAll={() => setSelected(new Set(approvableHashes))}
            onClear={() => setSelected(new Set())}
            onApprove={() => void onBulkApprove()}
            progress={progress}
          />
          <div className="rvq__list" data-testid="review-queue">
            {pager.pageItems.map((e) => (
              <ReviewRow
                key={e.documentHash}
                row={e}
                studioName={studioNameByTenantId.get(e.tenantId)}
                selectable={!e.needsFullHumanExtraction}
                checked={selected.has(e.documentHash)}
                onToggle={() => toggle(e.documentHash)}
                bulkBusy={busy}
              />
            ))}
          </div>
          <Pagination
            page={pager.page}
            pageCount={pager.pageCount}
            onPageChange={pager.setPage}
            total={pager.total}
            rangeStart={pager.rangeStart}
            rangeEnd={pager.rangeEnd}
            itemLabel="item"
          />
        </>
      )}
    </>
  );
}

// [Phase 4] Bulk-approve toolbar for onboarding/backlog triage. Acts only on the
// high-confidence (non-hybrid) entries; hybrid docs need per-file clause entry
// and are excluded. Sequential approve (each embeds into the L2 vector store) —
// fine at studio backlog scale (tens–low hundreds); a server batch endpoint is
// the escape hatch past that.
function BulkToolbar({
  approvableHashes,
  selected,
  onSelectAll,
  onClear,
  onApprove,
  progress,
}: {
  approvableHashes: string[];
  selected: ReadonlySet<string>;
  onSelectAll: () => void;
  onClear: () => void;
  onApprove: () => void;
  progress: { done: number; total: number } | null;
}): JSX.Element {
  const selectedCount = approvableHashes.filter((h) => selected.has(h)).length;
  const allSelected =
    approvableHashes.length > 0 && selectedCount === approvableHashes.length;
  const busy = progress !== null;
  return (
    <div className="rvq__bulk" data-testid="rvq-bulk-toolbar">
      <Checkbox
        className="rvq__bulk__all ds-checkbox"
        checked={allSelected}
        disabled={busy || approvableHashes.length === 0}
        onCheckedChange={(c) => (c ? onSelectAll() : onClear())}
        label={
          <>
            Select all high-confidence
            <em className="rvq__bulk__hint">
              {" "}
              ({approvableHashes.length} auto-extracted)
            </em>
          </>
        }
      />
      <div className="rvq__bulk__spacer" />
      {busy ? (
        <span className="rvq__bulk__progress" role="status">
          Approving {progress.done}/{progress.total}…
        </span>
      ) : (
        selectedCount > 0 && (
          <span className="rvq__bulk__count">{selectedCount} selected</span>
        )
      )}
      <Button
        variant="primary"
        className="btn--xs"
        disabled={busy || selectedCount === 0}
        onClick={onApprove}
        data-testid="rvq-bulk-approve"
      >
        {busy ? "Approving…" : `Approve ${selectedCount || ""} selected`}
      </Button>
    </div>
  );
}


// One queue card: the auto-detected taxonomy shown as editable, vocabulary-
// grounded fields, plus approve / reject. The reviewer only sends
// a tag override for fields they actually changed, so an untouched file keeps
// the upload/LLM precedence server-side.
// [S-632] Fetch the doc-in-review's source bytes (auth headers carried by
// useApiBlob) and open the type-aware in-app SourceViewer so a reviewer can
// read the document without leaving the categorization screen. Keyed by the
// entry's documentHash; the route gates on the doc's own tenant.
function useReviewSource(documentHash: string): {
  openViewer: () => void;
  opening: boolean;
  error: string | null;
  blob: Blob | null;
  viewerOpen: boolean;
  setViewerOpen: (open: boolean) => void;
} {
  const fetchBlob = useApiBlob("admin");
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  function openViewer(): void {
    setError(null);
    setOpening(true);
    fetchBlob(`/review-queue/${encodeURIComponent(documentHash)}/source`)
      .then(({ blob: b }) => {
        setBlob(b);
        setViewerOpen(true);
      })
      .catch((e: unknown) => {
        const msg =
          e instanceof ApiError && e.status === 404
            ? "No source file is stored for this document."
            : e instanceof ApiError && e.status === 501
              ? "Document preview isn't available in this environment."
              : e instanceof Error
                ? e.message
                : "Couldn't open the source document.";
        setError(msg);
      })
      .finally(() => setOpening(false));
  }

  return { openViewer, opening, error, blob, viewerOpen, setViewerOpen };
}

function ReviewRow({
  row,
  studioName,
  selectable,
  checked,
  onToggle,
  bulkBusy,
}: {
  row: ReviewQueueEntryRow;
  studioName: string | undefined;
  selectable: boolean;
  checked: boolean;
  onToggle: () => void;
  bulkBusy: boolean;
}): JSX.Element {
  const approve = useApproveReviewEntry();
  const toast = useToast();
  const reject = useRejectReviewEntry();
  // [bugfix] The catalog reads that fed the old Deal type / Guild selects are
  // gone with them: contract_type ids and facet.guild ids are a different
  // naming universe from what Stage 2 emits and what precedentTerms store.
  // [Phase 3b] Ground the OPEN-dimension correction inputs in the studio's own
  // inferred vocabulary: the reviewer sees the established values (so they
  // correct *toward* them when it's the same concept) and can still type a
  // genuinely-new value (the escape valve for a mis-grounded role/instrument).
  const vocab = useStudioVocabulary(row.tenantId);
  // [S-632] In-screen source preview for this document.
  const source = useReviewSource(row.documentHash);

  const [dealType, setDealType] = useState<string>(row.dealType ?? "");
  const [guild, setGuild] = useState<string>(row.guild ?? "");
  const [instrumentType, setInstrumentType] = useState<string>(
    row.instrumentType ?? ""
  );
  const [counterpartyRole, setCounterpartyRole] = useState<string>(
    row.counterpartyRole ?? ""
  );
  const [budgetBand, setBudgetBand] = useState<string>(row.budgetBand ?? "");
  const [talentTier, setTalentTier] = useState<string>(row.talentTier ?? "");
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  const busy = approve.isPending || reject.isPending || bulkBusy;
  // Reject errors surface inside the reject dialog; the inline pill is for
  // approve only.
  const error = approve.isError ? approve.error : null;

  // [Phase 4] Novelty signal for bulk triage: which auto-detected values are NOT
  // yet in the studio's established vocabulary — the entries worth a human look
  // before bulk-accepting. Suppressed on a cold corpus (precedentCount === 0),
  // where every value is trivially "new" and the signal is meaningless.
  const vocabReady = vocab.data !== undefined && vocab.data.precedentCount > 0;
  const isNovel = (
    value: string | null,
    facets: PrecedentFacet[] | undefined
  ): boolean =>
    value != null &&
    value.length > 0 &&
    !(facets ?? []).some((f) => f.value === value);
  const novelties = !vocabReady
    ? []
    : [
        isNovel(row.dealType, vocab.data?.dealType) ? `deal: ${row.dealType}` : null,
        isNovel(row.instrumentType, vocab.data?.instrumentType)
          ? `instrument: ${row.instrumentType}`
          : null,
        isNovel(row.counterpartyRole, vocab.data?.counterpartyRole)
          ? `role: ${row.counterpartyRole}`
          : null,
      ].filter((x): x is string => x !== null);

  const facetValues = (f: PrecedentFacet[] | undefined): string[] =>
    (f ?? []).map((x) => x.value);

  const onApprove = (): void => {
    // Send only fields the reviewer changed from the auto-detected value.
    // tags → precedentTerms (dealType/guild/tier); dimensions → Contract.dimensions.
    const tags: ApproveTags = {};
    if (dealType.length > 0 && dealType !== (row.dealType ?? "")) {
      tags.dealType = dealType;
    }
    if (guild.length > 0 && guild !== (row.guild ?? "")) {
      tags.guild = guild;
    }
    if (talentTier.trim().length > 0 && talentTier.trim() !== (row.talentTier ?? "")) {
      tags.talentTier = talentTier.trim();
    }
    const dimensions: ApproveDimensions = {};
    if (
      instrumentType.trim().length > 0 &&
      instrumentType.trim() !== (row.instrumentType ?? "")
    ) {
      dimensions.instrumentType = instrumentType.trim();
    }
    if (
      counterpartyRole.trim().length > 0 &&
      counterpartyRole.trim() !== (row.counterpartyRole ?? "")
    ) {
      dimensions.counterpartyRole = counterpartyRole.trim();
    }
    if (budgetBand.trim().length > 0 && budgetBand.trim() !== (row.budgetBand ?? "")) {
      dimensions.budgetBand = budgetBand.trim();
    }
    approve.mutate(
      {
        documentHash: row.documentHash,
        ...(Object.keys(tags).length > 0 ? { tags } : {}),
        ...(Object.keys(dimensions).length > 0 ? { dimensions } : {}),
      },
      {
        // [S-634] The approved row drops out of the queue on refetch — confirm
        // success + name what was indexed + where to find it.
        onSuccess: () => {
          toast.push({
            variant: "success",
            title: `Approved · ${row.originalFileName ?? "document"}`,
            description: (
              <>
                {/* Plain anchor: a toast renders in a portal OUTSIDE the Router,
                so <Link> has no router context (main.tsx nests ToastProvider
                above BrowserRouter). A full nav to the library is fine here. */}
            Added to your <a href="/precedent">precedent library →</a>
              </>
            ),
          });
        },
      }
    );
  };

  const openReject = (): void => {
    reject.reset();
    setReason("");
    setRejecting(true);
  };

  const closeReject = (): void => {
    reject.reset();
    setRejecting(false);
  };

  const submitReject = (): void => {
    const trimmed = reason.trim();
    if (trimmed.length === 0 || reject.isPending) return;
    reject.mutate(
      { documentHash: row.documentHash, reason: trimmed },
      { onSuccess: () => setRejecting(false) }
    );
  };

  return (
    <div className="rvq__card" data-testid={`queue-row-${row.documentId}`}>
      <div className="rvq__card__head">
        {selectable ? (
          <Checkbox
            className="rvq__card__check ds-checkbox"
            checked={checked}
            disabled={bulkBusy}
            onCheckedChange={() => onToggle()}
            aria-label={`Select ${row.documentId} for bulk approve`}
          />
        ) : (
          <span
            className="rvq__badge rvq__badge--manual"
            title="This document needs per-file clause entry and can't be bulk-approved."
          >
            Manual extraction
          </span>
        )}
        {/* [S-631] Lead with the original filename — the identity a reviewer
            categorizes by. Falls back to a placeholder for legacy docs with no
            captured name. */}
        <span
          className="rvq__card__file"
          title={row.originalFileName ?? "Unnamed document"}
          data-testid={`queue-file-${row.documentId}`}
        >
          {row.originalFileName ?? "Unnamed document"}
        </span>
        {/* [S-632] Read the document without leaving the categorization screen. */}
        <Button
          variant="bare"
          className="rvq__card__open"
          onClick={source.openViewer}
          disabled={source.opening}
          data-testid={`queue-open-${row.documentId}`}
        >
          {source.opening ? "Opening…" : "Open document ↗"}
        </Button>
        {/* Only ever renders for platform_admin (cross-tenant view) — a
            tenant_admin's queue is already scoped to their one studio. */}
        {studioName !== undefined && (
          <code className="rvq__card__tenant">{studioName}</code>
        )}
        {novelties.length > 0 && (
          <span
            className="rvq__badge rvq__badge--novel"
            data-testid="rvq-novelty"
            title="Auto-detected values not yet in your studio's vocabulary — worth a look before approving."
          >
            New: {novelties.join(" · ")}
          </span>
        )}
        <span className="rvq__card__when">{formatRelative(row.enqueuedAt)}</span>
      </div>

      {source.error !== null && (
        <p className="rvq__source-err" role="status" data-testid={`queue-open-err-${row.documentId}`}>
          {source.error}
        </p>
      )}
      {/* [S-632] Type-aware in-app viewer (PDF / DOCX / text / download). */}
      <SourceViewer
        open={source.viewerOpen}
        onOpenChange={source.setViewerOpen}
        blob={source.blob}
        baseName={row.originalFileName ?? "document"}
      />

      <div className="rvq__tags">
        {/* [bugfix] Deal type and Guild were Selects fed from the CATALOG
            (contract_type ids like "actor-agreement"; facet.guild ids like
            "sag-aftra"), while Stage 2 extracts "feature_film" and "SAG".
            The auto-detected value could never be one of the options, so both
            rendered blank next to four comboboxes that worked — and picking
            anything replaced a production type with a contract kind. They are
            grounded in the studio vocabulary now, the same source as their
            neighbours and the same values precedent search filters on. */}
        <VocabCombobox
          id={"deal-" + row.documentHash}
          label="Deal type"
          auto={row.dealType}
          value={dealType}
          onChange={setDealType}
          suggestions={facetValues(vocab.data?.dealType)}
        />
        <VocabCombobox
          id={"guild-" + row.documentHash}
          label="Guild"
          auto={row.guild}
          value={guild}
          onChange={setGuild}
          suggestions={facetValues(vocab.data?.guild)}
        />

        {/* [Phase 3b] OPEN comparability dimensions — grounded comboboxes:
            suggestions are the studio's existing values; new values are allowed
            (the escape valve for a mis-grounded role / instrument / band). */}
        <VocabCombobox
          id={`it-${row.documentHash}`}
          label="Instrument type"
          auto={row.instrumentType}
          value={instrumentType}
          onChange={setInstrumentType}
          suggestions={facetValues(vocab.data?.instrumentType)}
        />
        <VocabCombobox
          id={`role-${row.documentHash}`}
          label="Counterparty role"
          auto={row.counterpartyRole}
          value={counterpartyRole}
          onChange={setCounterpartyRole}
          suggestions={facetValues(vocab.data?.counterpartyRole)}
        />
        <VocabCombobox
          id={`tier-${row.documentHash}`}
          label="Talent tier"
          auto={row.talentTier}
          value={talentTier}
          onChange={setTalentTier}
          suggestions={facetValues(vocab.data?.tier)}
        />
        <VocabCombobox
          id={`budget-${row.documentHash}`}
          label="Budget band"
          auto={row.budgetBand}
          value={budgetBand}
          onChange={setBudgetBand}
          suggestions={facetValues(vocab.data?.budgetBand)}
        />
      </div>

      <div className="rvq__actions">
        <Button
          variant="primary"
          className="btn--xs"
          disabled={busy}
          onClick={onApprove}
        >
          {approve.isPending ? "Approving…" : "Approve & index"}
        </Button>
        <Button
          variant="ghost"
          className="btn--xs"
          disabled={busy}
          onClick={openReject}
        >
          {reject.isPending ? "Rejecting…" : "Reject"}
        </Button>
        {error !== null && (
          <span className="rvq__actions__err" role="alert">
            {getApiErrorMessage(error) ?? "Action failed"}
          </span>
        )}
      </div>

      <Dialog
        open={rejecting}
        onOpenChange={(isOpen) => {
          if (!isOpen) closeReject();
        }}
        title="Reject this document?"
        description="It will be marked failed and not indexed as a precedent. The original upload is retained for audit."
      >
        <div className="rvq__reject">
          <label htmlFor={`rvq-reject-${row.documentHash}`}>Reason</label>
          <textarea
            id={`rvq-reject-${row.documentHash}`}
            data-testid="rvq-reject-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Wrong document type, duplicate, or unreadable scan"
          />
        </div>
        {reject.isError && (
          <div className="rvq__reject__err" role="alert" data-testid="rvq-reject-error">
            {getApiErrorMessage(reject.error) ?? "Couldn't reject document"}
          </div>
        )}
        <div className="rvq__reject__ft">
          <Button
            variant="ghost"
            type="button"
            disabled={reject.isPending}
            onClick={closeReject}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            disabled={reason.trim().length === 0 || reject.isPending}
            onClick={submitReject}
            data-testid="rvq-reject-confirm"
          >
            {reject.isPending ? "Rejecting…" : "Reject document"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

// [Phase 3b] An editable combobox for an OPEN comparability dimension: a free
// text input backed by a <datalist> of the studio's existing values. Picking a
// suggestion canonicalizes toward the established vocabulary; typing a new value
// introduces one deliberately — the spec's "accept / remap / introduce" made
// concrete (CSAI_PrecedentClauseModel_Spec.md §5). Shows the LLM's auto value.
function VocabCombobox({
  id,
  label,
  auto,
  value,
  onChange,
  suggestions,
}: {
  id: string;
  label: string;
  auto: string | null;
  value: string;
  onChange: (v: string) => void;
  suggestions: string[];
}): JSX.Element {
  const listId = `vocab-${id}`;
  return (
    <label className="rvq__field">
      <span className="rvq__field__k">
        {label}
        {auto != null && <em className="rvq__auto">auto: {auto}</em>}
      </span>
      <input
        className="rvq__input"
        type="text"
        aria-label={label}
        list={listId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="—"
        autoComplete="off"
      />
      <datalist id={listId}>
        {suggestions.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </label>
  );
}

function formatRelative(iso: string): string {
  const diffMs = Date.now() - Date.parse(iso);
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
